import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { setAdminCsrfToken } from "../lib/adminApi";

export default function AdminLogin() {
  const [passphrase, setPassphrase] = useState("");
  const [totp, setTotp] = useState("");
  const [totpConfigured, setTotpConfigured] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  useEffect(() => {
    fetch("/api/admin/me", { credentials: "include" })
      .then((r) => r.json())
      .then((data: { totpConfigured?: boolean; authenticated?: boolean }) => {
        if (data.authenticated) {
          setLocation("/admin");
          return;
        }
        setTotpConfigured(Boolean(data.totpConfigured));
      })
      .catch(() => {
        /* ignore — login form still works */
      });
  }, [setLocation]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          passphrase,
          ...(totp.trim() ? { totp: totp.trim() } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        csrfToken?: string;
        totpRequired?: boolean;
      };
      if (!res.ok) {
        if (data.totpRequired) setTotpConfigured(true);
        setError(data.error ?? "Invalid passphrase");
        return;
      }
      // Stash CSRF token immediately so the very next admin mutation works
      // without waiting for AdminGuard's /api/admin/me probe.
      if (data.csrfToken) setAdminCsrfToken(data.csrfToken);
      setLocation("/admin");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-dark.svg" alt="Zindeal" className="w-12 h-12 rounded-full" />
          <h1 className="text-white text-2xl font-bold tracking-tight">Zindeal</h1>
          <p className="text-gray-400 text-sm mt-1">Platform Owner Console</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="h-4 w-4 text-gray-400" />
            <h2 className="text-gray-100 font-semibold text-sm">Secure Access</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Owner Passphrase
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  required
                  autoFocus
                  placeholder="Enter passphrase…"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {(totpConfigured || totp.length > 0) && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Authenticator code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required={totpConfigured}
                  placeholder="6-digit code"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 tracking-widest"
                />
              </div>
            )}

            {error && (
              <div className="text-red-400 text-xs bg-red-950/50 border border-red-900 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !passphrase || (totpConfigured && totp.length !== 6)}
              className="w-full bg-[#0B1834] hover:bg-[#0A152E] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Authenticating…</>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          This area is restricted to platform owners only.
        </p>
      </div>
    </div>
  );
}
